import React, { useMemo, useCallback, useRef, useEffect } from 'react'
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Libraries } from '@react-google-maps/api'

// Must match the libraries used in GooglePlacesAutocomplete to avoid loader conflicts
const libraries: Libraries = ['places']

interface LocationData {
  uuid: string
  latitude: string
  longitude: string
  name?: string
  address?: string
}

interface Activity {
  uuid: string
  name?: string
  locations?: LocationData
}

interface Accommodation {
  uuid: string
  name: string
  locations?: LocationData
}

interface TripMapProps {
  activities: Activity[]
  accommodations: Accommodation[]
  selectedMarker: string | null
  onSelectMarker: (markerId: string | null) => void
}

const containerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
}

const ZOOM_LEVEL_CLOSE = 18

const TripMap: React.FC<TripMapProps> = ({ activities, accommodations, selectedMarker, onSelectMarker }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY as string,
    libraries,
  })

  const mapRef = useRef<google.maps.Map | null>(null)

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map
  }, [])

  const markers = useMemo(() => {
    const result: Array<{
      id: string
      lat: number
      lng: number
      title: string
      address?: string
      type: 'accommodation' | 'activity'
    }> = []

    accommodations.forEach((acc) => {
      if (acc.locations?.latitude && acc.locations?.longitude) {
        result.push({
          id: `acc-${acc.uuid}`,
          lat: parseFloat(acc.locations.latitude),
          lng: parseFloat(acc.locations.longitude),
          title: acc.name,
          address: acc.locations.address,
          type: 'accommodation',
        })
      }
    })

    activities.forEach((act) => {
      if (act.locations?.latitude && act.locations?.longitude) {
        result.push({
          id: `act-${act.uuid}`,
          lat: parseFloat(act.locations.latitude),
          lng: parseFloat(act.locations.longitude),
          title: act.name || 'Activity',
          address: act.locations.address,
          type: 'activity',
        })
      }
    })

    return result
  }, [activities, accommodations])

  const center = useMemo(() => {
    if (markers.length === 0) {
      return { lat: 0, lng: 0 }
    }
    const sumLat = markers.reduce((sum, m) => sum + m.lat, 0)
    const sumLng = markers.reduce((sum, m) => sum + m.lng, 0)
    return {
      lat: sumLat / markers.length,
      lng: sumLng / markers.length,
    }
  }, [markers])

  // Zoom to selected marker when it changes
  useEffect(() => {
    if (selectedMarker && mapRef.current) {
      const marker = markers.find((m) => m.id === selectedMarker)
      if (marker) {
        mapRef.current.panTo({ lat: marker.lat, lng: marker.lng })
        mapRef.current.setZoom(ZOOM_LEVEL_CLOSE)
      }
    }
  }, [selectedMarker, markers])

  if (!isLoaded) {
    return (
      <div style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        color: '#666'
      }}>
        Loading map...
      </div>
    )
  }

  if (markers.length === 0) {
    return (
      <div style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        color: '#666'
      }}>
        No locations to display on map
      </div>
    )
  }

  return (
    <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={12} onLoad={onMapLoad}>
      {markers.map((marker) => (
        <Marker
          key={marker.id}
          position={{ lat: marker.lat, lng: marker.lng }}
          title={marker.title}
          onClick={() => onSelectMarker(marker.id)}
          icon={
            marker.type === 'accommodation'
              ? {
                  url: 'https://maps.google.com/mapfiles/kml/shapes/homegardenbusiness.png',
                  scaledSize: new google.maps.Size(32, 32),
                }
              : {
                  url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                  scaledSize: new google.maps.Size(32, 32),
                }
          }
        >
          {selectedMarker === marker.id && (
            <InfoWindow onCloseClick={() => onSelectMarker(null)}>
              <div style={{ padding: '4px' }}>
                <strong>{marker.title}</strong>
                {marker.address && (
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>
                    {marker.address}
                  </p>
                )}
                <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#999' }}>
                  {marker.type === 'accommodation' ? 'Accommodation' : 'Activity'}
                </p>
              </div>
            </InfoWindow>
          )}
        </Marker>
      ))}
    </GoogleMap>
  )
}

export default TripMap
